import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Share, Copy, ArrowClockwise, ChartLineUp, Confetti, Users,
  Clock, HourglassMedium, ImageSquare, DownloadSimple, WhatsappLogo,
  ChatCircleDots, Trophy, MedalMilitary, Fire,
} from "@phosphor-icons/react";
import client from "../lib/api";

/*
  AccountReferralPage — carrier-facing referral dashboard.
  Route: /app/referrals

  Shows the user's unique link, share message, per-user stats
  (visits, conversions, days earned), and a list of who they've referred.
*/

export default function AccountReferralPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegenerating] = useState(false);
  const [copiedField, setCopiedField] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await client.get("/account/referral");
      setData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load your referral link");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const copy = async (field, text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      toast.success(`${field === "link" ? "Link" : "Share message"} copied`);
      setTimeout(() => setCopiedField(null), 1600);
    } catch {
      toast.error("Copy failed — select and copy manually");
    }
  };

  const shareNative = async () => {
    if (!navigator.share) {
      copy("link", data.link);
      return;
    }
    try {
      await navigator.share({
        title: "FreightPilot AI",
        text: data.share_message,
        url: data.link,
      });
    } catch {/* user canceled — no-op */}
  };

  const regenerate = async () => {
    if (!window.confirm(
      "Regenerate your referral link?\n\nAny old copies (in emails, texts, or shared posts) will STOP working. Only do this if you want to invalidate a leaked or spammed link."
    )) return;
    setRegenerating(true);
    try {
      await client.post("/account/referral/regenerate");
      toast.success("New link generated");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Regenerate failed");
    } finally {
      setRegenerating(false);
    }
  };

  if (loading) {
    return <div className="p-6 text-neutral-500 text-sm">Loading your referral dashboard…</div>;
  }
  if (!data) return null;

  const { stats, conversions = [], referrer_reward_days, referred_reward_days, milestones } = data;

  return (
    <div className="max-w-5xl mx-auto p-4 sm:p-6 space-y-6" data-testid="referral-page">
      {/* MILESTONE CELEBRATION — fires exactly once per achievement */}
      <MilestoneCelebrator milestones={milestones} onAcknowledged={load} />

      <div>
        <div className="overline mb-1 flex items-center gap-2">
          <Confetti size={11} weight="fill" /> INVITE A FLEET
        </div>
        <h1 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight">Refer another carrier</h1>
        <p className="text-neutral-500 text-sm mt-1 max-w-2xl">
          Send this link to any dispatcher, owner-op, or fleet you know. When they sign up:
          <span className="text-white font-medium"> both of you get {referrer_reward_days}-day trial extensions</span> —
          added automatically when they start their trial.{" "}
          <a
            href="/referrals/leaderboard"
            target="_blank"
            rel="noreferrer"
            className="text-[#FF9764] hover:text-[#FFB08A] underline underline-offset-2 whitespace-nowrap"
            data-testid="referral-leaderboard-link"
          >
            See the public leaderboard →
          </a>
        </p>
      </div>

      {/* MONTHLY CONTEST BANNER */}
      <MonthlyContestBanner />

      {/* MILESTONE PROGRESS STRIP */}
      <MilestoneProgress
        milestones={milestones}
        currentConversions={stats.conversions}
      />

      {/* LINK CARD */}
      <div className="card-surface p-6 sm:p-8" data-testid="referral-link-card">
        <div className="overline mb-3 flex items-center gap-2 text-[#FF5A1F]">
          <Share size={12} weight="fill" /> YOUR REFERRAL LINK
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <div
            className="flex-1 min-w-0 rounded-lg bg-white/[0.03] border border-white/10 px-4 py-3 text-sm text-white font-mono truncate"
            data-testid="referral-link-input"
            title={data.link}
          >
            {data.link}
          </div>
          <button
            onClick={() => copy("link", data.link)}
            data-testid="copy-link-btn"
            className="shrink-0 inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold bg-[#FF5A1F] text-black hover:bg-[#FF7038]"
          >
            <Copy size={13} weight="fill" /> {copiedField === "link" ? "Copied!" : "Copy"}
          </button>
          <button
            onClick={shareNative}
            data-testid="share-btn"
            className="shrink-0 inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm border border-white/15 hover:border-white/35 hover:bg-white/5"
          >
            <Share size={13} weight="fill" /> Share
          </button>
        </div>
        <div className="mt-5 pt-4 border-t border-white/10">
          <div className="overline mb-2 text-neutral-500">Copy-paste share message</div>
          <div className="text-sm text-neutral-300 bg-white/[0.02] rounded-lg p-3 leading-relaxed border border-white/[0.05]" data-testid="referral-share-message">
            "{data.share_message}"
          </div>
          <div className="mt-3 flex items-center gap-2">
            <button
              onClick={() => copy("message", data.share_message)}
              data-testid="copy-message-btn"
              className="text-xs px-3 py-1.5 rounded-md border border-white/10 text-neutral-300 hover:text-white hover:border-white/30 inline-flex items-center gap-1"
            >
              <Copy size={11} weight="fill" /> {copiedField === "message" ? "Copied!" : "Copy message"}
            </button>
            <button
              onClick={regenerate}
              disabled={regenerating}
              data-testid="regenerate-code-btn"
              className="text-xs px-3 py-1.5 rounded-md border border-white/10 text-neutral-500 hover:text-white hover:border-white/30 inline-flex items-center gap-1 disabled:opacity-50"
              title="Only if your link was leaked or spammed"
            >
              <ArrowClockwise size={11} weight="bold" className={regenerating ? "animate-spin" : ""} />
              {regenerating ? "Regenerating…" : "Regenerate link"}
            </button>
          </div>
        </div>
      </div>

      {/* STATS */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard icon={Users} label="Link clicks" value={stats.visits} />
        <StatCard icon={Confetti} label="Signups" value={stats.conversions} accent="orange" />
        <StatCard icon={ChartLineUp} label="Conversion rate" value={`${stats.conversion_rate_pct}%`} />
        <StatCard icon={Clock} label="Days earned" value={`+${stats.days_earned}`} accent="green" />
      </div>

      {/* SHARE CARD */}
      <ShareCardBlock
        referrerRewardDays={referrer_reward_days}
        referredRewardDays={referred_reward_days}
        link={data.link}
        shareMessage={data.share_message}
      />

      {/* CONVERSIONS LIST */}
      <div className="card-surface p-4 sm:p-6" data-testid="conversions-list">
        <div className="flex items-center justify-between mb-3">
          <div className="overline flex items-center gap-2">
            <HourglassMedium size={11} weight="fill" /> WHO YOU'VE REFERRED
          </div>
          <div className="text-xs text-neutral-500">{conversions.length} total</div>
        </div>
        {conversions.length === 0 ? (
          <div className="text-sm text-neutral-500 italic px-2 py-6 text-center">
            No signups yet. Share the link with someone running dispatch and both of you land {referred_reward_days} extra trial days.
          </div>
        ) : (
          <div className="divide-y divide-white/5">
            {conversions.map((c) => (
              <div key={c.id} className="py-3 px-2 flex items-center gap-3" data-testid={`conversion-${c.id}`}>
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#FF5A1F] to-[#B54313] flex items-center justify-center text-black font-semibold text-xs shrink-0">
                  {(c.referred_email || "?")[0].toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-white font-medium truncate">{c.referred_email}</div>
                  <div className="text-[11px] text-neutral-500 mt-0.5">
                    Signed up {fmtRel(c.converted_at)} · You earned +{c.referrer_reward_days}d
                  </div>
                </div>
                <RewardPill status={c.referrer_reward_status} />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* ─── MILESTONE CELEBRATION ─── */

/* Fires a rich Sonner toast for every un-acknowledged milestone the user has
   crossed. Once shown, acknowledges them on the backend so the toast never
   repeats — even if the user refreshes the page or comes back tomorrow. */
function MilestoneCelebrator({ milestones, onAcknowledged }) {
  useEffect(() => {
    const unseen = milestones?.unseen || [];
    if (unseen.length === 0) return;

    // Stagger the toasts so multiple milestones celebrate one-by-one instead
    // of stacking on top of each other. Rare (users cross one at a time in
    // practice) but bulletproof for late-onboarded pilots seeded with data.
    unseen.forEach((m, i) => {
      setTimeout(() => {
        toast.success(m.headline || `+${m.bonus_days} bonus trial days!`, {
          description: m.trial_extended
            ? `We just added ${m.bonus_days} days to your trial. Keep going.`
            : `Your milestone is banked. Admin will convert to account credit.`,
          duration: 8000,
        });
      }, 400 + i * 900);
    });

    // Acknowledge server-side after the last toast has surfaced
    const ackDelay = 400 + unseen.length * 900 + 200;
    const t = setTimeout(() => {
      client
        .post("/account/referral/milestones/ack")
        .then(() => onAcknowledged && onAcknowledged())
        .catch(() => {});
    }, ackDelay);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(milestones?.unseen || [])]);

  return null;
}


/* ─── MILESTONE PROGRESS STRIP ─── */

/* A slim always-visible strip that renders under the contest banner and
   shows how far the pilot is from their next milestone (5 / 10 / 25).
   Silent when a user has already blown past the top tier — no reason to
   render an empty bar. */
function MilestoneProgress({ milestones, currentConversions }) {
  if (!milestones) return null;
  const { tiers = [], reached = [], next_tier, next_tier_remaining } = milestones;
  if (!next_tier || tiers.length === 0) return null;

  const reachedIds = new Set(reached.map((m) => m.threshold));
  const hasReached = reached.length > 0;
  const highestReached = hasReached
    ? reached.reduce((max, m) => (m.threshold > max ? m.threshold : max), 0)
    : null;
  const prevThreshold = [...tiers].reverse().find((t) => currentConversions >= t.threshold)?.threshold || 0;
  const spanTotal = next_tier.threshold - prevThreshold;
  const spanDone = Math.max(0, currentConversions - prevThreshold);
  const pct = Math.min(100, Math.max(6, Math.round((spanDone / spanTotal) * 100)));

  const downloadFlexCard = async () => {
    try {
      const res = await client.get(
        `/account/referral/milestone-card.png${highestReached ? `?level=${highestReached}` : ""}`,
        { responseType: "blob" }
      );
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `freightpilotai-milestone-${highestReached || 5}.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Milestone flex card downloaded (${highestReached} signups)`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't render milestone card");
    }
  };

  return (
    <div
      data-testid="milestone-progress-strip"
      className="rounded-xl border border-white/10 bg-white/[0.02] p-4 sm:p-5"
    >
      <div className="flex items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-2 min-w-0">
          <MedalMilitary size={14} weight="fill" className="text-[#FF9764] shrink-0" />
          <div className="text-sm sm:text-base text-white font-medium truncate">
            {next_tier_remaining === 1
              ? "1 signup away from your next milestone"
              : `${next_tier_remaining} signups from +${next_tier.bonus_days} bonus days`}
          </div>
        </div>
        <div className="text-xs text-neutral-500 tabular-nums shrink-0">
          {currentConversions} / {next_tier.threshold}
        </div>
      </div>
      <div className="relative h-2 rounded-full bg-white/5 overflow-hidden mb-3">
        <div
          className="absolute inset-y-0 left-0 bg-gradient-to-r from-[#FF5A1F] to-[#FF9764] rounded-full transition-[width] duration-500"
          style={{ width: `${pct}%` }}
          data-testid="milestone-progress-bar"
        />
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {tiers.map((t) => {
          const done = reachedIds.has(t.threshold);
          const isNext = t.threshold === next_tier.threshold;
          return (
            <div
              key={t.threshold}
              data-testid={`milestone-tier-${t.threshold}`}
              className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-medium border ${
                done
                  ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300"
                  : isNext
                  ? "border-[#FF5A1F]/40 bg-[#FF5A1F]/10 text-[#FF9764]"
                  : "border-white/10 text-neutral-500"
              }`}
            >
              {done ? (
                <Fire size={11} weight="fill" />
              ) : isNext ? (
                <MedalMilitary size={11} weight="fill" />
              ) : null}
              <span className="tabular-nums">{t.threshold}</span> · +{t.bonus_days}d
            </div>
          );
        })}
      </div>
      {hasReached && (
        <div className="mt-4 flex items-center justify-between gap-3 rounded-lg border border-emerald-500/20 bg-emerald-500/5 px-3 py-2.5">
          <div className="text-xs text-emerald-200 min-w-0">
            🏆 You've crossed <span className="font-semibold tabular-nums">{highestReached}</span> signups — grab your flex card to post on socials.
          </div>
          <button
            type="button"
            onClick={downloadFlexCard}
            data-testid="download-milestone-card-btn"
            className="shrink-0 inline-flex items-center gap-1.5 rounded-full border border-emerald-400/40 bg-emerald-500/10 px-3 py-1.5 text-[11px] font-medium text-emerald-200 hover:bg-emerald-500/20 transition-colors"
          >
            <DownloadSimple size={12} weight="bold" /> Flex card
          </button>
        </div>
      )}
    </div>
  );
}


/* ─── MONTHLY CONTEST BANNER ─── */
function MonthlyContestBanner() {
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    client
      .get("/referrals/leaderboard/monthly", { params: { limit: 5 } })
      .then((r) => {
        if (cancelled) return;
        setData(r.data || null);
      })
      .catch(() => setData(null));
    return () => {
      cancelled = true;
    };
  }, []);

  // Silent when the endpoint fails — better than a broken banner
  if (!data) return null;

  const { ranked = [], month, total_this_month = 0 } = data;

  return (
    <div
      data-testid="monthly-contest-banner"
      className="relative rounded-2xl border border-[#FF5A1F]/30 bg-gradient-to-br from-[#FF5A1F]/[0.08] via-black/40 to-black/20 p-5 sm:p-6 overflow-hidden"
    >
      <div
        aria-hidden
        className="absolute inset-0 -z-10 opacity-50"
        style={{
          background:
            "radial-gradient(60% 60% at 15% 40%, rgba(255,90,31,0.14) 0%, rgba(255,90,31,0) 65%)",
        }}
      />
      <div className="grid gap-4 md:grid-cols-[1fr_auto] md:items-center">
        <div>
          <div className="overline mb-1.5 flex items-center gap-1.5 text-[#FF9764]">
            <Trophy size={12} weight="fill" /> Monthly Recruiter Contest · {month}
          </div>
          <h3 className="font-display text-xl sm:text-2xl font-semibold tracking-tight leading-snug">
            Top 5 this month get{" "}
            <span className="text-[#FF5A1F]">a free FreightPilot AI hat</span> + a shout-out on the founder digest.
          </h3>
          <p className="text-neutral-400 text-sm mt-1.5">
            {total_this_month === 0
              ? "Nobody has signed up yet this month — first referrer takes #1."
              : `${total_this_month} signup${total_this_month === 1 ? "" : "s"} so far. Every referral this month counts.`}
          </p>
        </div>
        <ol className="space-y-1.5 md:min-w-[240px]" data-testid="monthly-contest-list">
          {ranked.length === 0 ? (
            <li className="text-xs text-neutral-500 italic">
              No signups yet this month · claim rank #1
            </li>
          ) : (
            ranked.map((r) => (
              <li
                key={r.rank}
                data-testid={`monthly-contest-row-${r.rank}`}
                className="flex items-center gap-2.5 text-sm text-neutral-200"
              >
                <span
                  className={`inline-flex items-center justify-center h-6 w-6 rounded-full text-[10px] font-bold shrink-0 ${
                    r.rank === 1
                      ? "bg-[#FF5A1F] text-black"
                      : r.rank === 2
                      ? "bg-neutral-300 text-black"
                      : r.rank === 3
                      ? "bg-amber-700 text-white"
                      : "bg-white/10 text-neutral-300"
                  }`}
                >
                  {r.rank}
                </span>
                <span className="flex-1 truncate">{r.display_name}</span>
                <span className="text-xs text-neutral-400 tabular-nums">
                  {r.conversions} signup{r.conversions === 1 ? "" : "s"}
                </span>
              </li>
            ))
          )}
        </ol>
      </div>
    </div>
  );
}

// Silent, fire-and-forget share-card event tracker. Backend logs to
// `share_card_events` for the founder analytics dashboard. Never blocks
// the UI or surfaces errors — the actual share still happened either way.
function recordShareEvent(style, channel) {
  client
    .post("/account/referral/share-events", { style, channel })
    .catch(() => {});
}

/* ─── SHARE CARD BLOCK ─── */

// The 3 available card style variants — must stay in sync with the backend
// `_SHARE_STYLES` dict in routers/referrals.py.
const CARD_STYLES = [
  { id: "bold", label: "Bold", swatch: "bg-[#FF5A1F]" },
  { id: "minimalist", label: "Minimalist", swatch: "bg-neutral-100 border border-neutral-300" },
  { id: "holiday", label: "Holiday", swatch: "bg-gradient-to-br from-emerald-800 to-red-600" },
];

function ShareCardBlock({ referrerRewardDays, referredRewardDays, link, shareMessage }) {
  const [previewUrl, setPreviewUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [style, setStyle] = useState("bold");

  const buildBlobUrl = async (nextStyle = style) => {
    setLoading(true);
    try {
      // client returns axios instance with auth header; use responseType blob.
      const res = await client.get(`/account/referral/share-card.png?style=${nextStyle}`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      setPreviewUrl(url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't render share card");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    buildBlobUrl(style);
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [style]);

  const download = () => {
    if (!previewUrl) return;
    const a = document.createElement("a");
    a.href = previewUrl;
    a.download = `freightpilotai-referral-${style}.png`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    toast.success("Share card downloaded");
    recordShareEvent(style, "download");
  };

  const shareNative = async () => {
    if (!previewUrl) return;
    try {
      const blob = await fetch(previewUrl).then((r) => r.blob());
      const file = new File([blob], `freightpilotai-referral-${style}.png`, { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: "FreightPilot AI",
          text: `Grab ${(referrerRewardDays || 0) + 14}-day free trial via my link.`,
        });
        recordShareEvent(style, "native");
      } else {
        download();
      }
    } catch {
      // User canceled — no-op
    }
  };

  // WhatsApp quick share — opens https://wa.me with the referral message
  // pre-filled. Works everywhere WhatsApp is installed (mobile) or via
  // WhatsApp Web (desktop). No account/phone required from the sender.
  const shareWhatsApp = () => {
    const message = shareMessage || `Grab a free trial of FreightPilot AI with my link: ${link}`;
    const url = `https://wa.me/?text=${encodeURIComponent(message)}`;
    window.open(url, "_blank", "noopener,noreferrer");
    toast.success("Opening WhatsApp…");
    recordShareEvent(style, "whatsapp");
  };

  // iMessage / SMS quick share — opens the native SMS composer with the
  // referral message pre-filled. `sms:?body=…` works on iOS + Android and
  // gracefully falls back to nothing on desktop.
  const shareSMS = () => {
    const message = shareMessage || `Grab a free trial of FreightPilot AI with my link: ${link}`;
    // iOS prefers `&body=…`, Android accepts `?body=…`. This form works on both.
    const url = `sms:?&body=${encodeURIComponent(message)}`;
    window.location.href = url;
    toast.success("Opening Messages…");
    recordShareEvent(style, "sms");
  };

  return (
    <div className="card-surface p-6 sm:p-8" data-testid="referral-share-card-block">
      <div className="grid gap-6 md:grid-cols-[auto_1fr] md:items-start">
        <div className="mx-auto md:mx-0">
          <div
            className="w-40 sm:w-48 aspect-[4/5] rounded-xl overflow-hidden border border-white/10 bg-black/60 flex items-center justify-center"
            data-testid="referral-share-card-preview"
          >
            {loading || !previewUrl ? (
              <div className="text-xs text-neutral-500 animate-pulse">Rendering…</div>
            ) : (
              <img
                src={previewUrl}
                alt={`Referral share card preview (${style} style)`}
                className="w-full h-full object-cover"
              />
            )}
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="overline mb-2 flex items-center gap-2 text-[#FF5A1F]">
            <ImageSquare size={12} weight="fill" /> ONE-TAP SHARE CARD
          </div>
          <h2 className="font-display text-xl sm:text-2xl font-semibold tracking-tight mb-2">
            Post it. Text it. Slap it on your dashboard.
          </h2>
          <p className="text-neutral-400 text-sm leading-relaxed mb-4">
            A 1080×1350 branded card with your referral QR baked in. Drop it in an
            Instagram/TikTok/Reels post, WhatsApp, or a group text — recipients scan
            the QR and land on signup with your code already applied.
          </p>

          {/* Style picker */}
          <div className="mb-4" data-testid="share-card-style-picker">
            <div className="overline mb-2 text-neutral-500">Style</div>
            <div className="flex flex-wrap gap-2">
              {CARD_STYLES.map((s) => (
                <button
                  key={s.id}
                  onClick={() => setStyle(s.id)}
                  disabled={loading && style === s.id}
                  data-testid={`share-card-style-${s.id}`}
                  aria-pressed={style === s.id}
                  className={`inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${
                    style === s.id
                      ? "border border-[#FF5A1F] bg-[#FF5A1F]/10 text-white"
                      : "border border-white/10 text-neutral-400 hover:text-white hover:border-white/30"
                  }`}
                >
                  <span className={`h-3 w-3 rounded-full ${s.swatch}`} />
                  {s.label}
                </button>
              ))}
            </div>
          </div>

          <div className="flex flex-wrap gap-2.5">
            <button
              onClick={download}
              disabled={loading || !previewUrl}
              data-testid="download-share-card-btn"
              className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold bg-[#FF5A1F] text-black hover:bg-[#FF7038] disabled:opacity-50"
            >
              <DownloadSimple size={13} weight="bold" /> Download PNG
            </button>
            <button
              onClick={shareWhatsApp}
              disabled={!link}
              data-testid="whatsapp-share-btn"
              className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold bg-[#25D366] text-black hover:bg-[#22c15e] disabled:opacity-50"
            >
              <WhatsappLogo size={14} weight="fill" /> WhatsApp
            </button>
            <button
              onClick={shareSMS}
              disabled={!link}
              data-testid="sms-share-btn"
              className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold bg-[#3B82F6] text-white hover:bg-[#2563EB] disabled:opacity-50"
            >
              <ChatCircleDots size={13} weight="fill" /> iMessage / SMS
            </button>
            <button
              onClick={shareNative}
              disabled={loading || !previewUrl}
              data-testid="native-share-card-btn"
              className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm border border-white/15 hover:border-white/35 hover:bg-white/5 disabled:opacity-50"
            >
              <Share size={13} weight="fill" /> More
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── SMALL COMPONENTS ─── */
function StatCard({ icon: Icon, label, value, accent }) {
  const color =
    accent === "orange" ? "text-[#FF5A1F]"
    : accent === "green" ? "text-emerald-400"
    : "text-white";
  return (
    <div className="card-surface p-4" data-testid={`stat-${label.replace(/\s+/g, "-").toLowerCase()}`}>
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">
        <Icon size={11} weight="fill" /> {label}
      </div>
      <div className={`text-2xl sm:text-3xl font-display font-semibold tracking-tight mt-1 ${color}`}>
        {value}
      </div>
    </div>
  );
}

function RewardPill({ status }) {
  const styles = {
    granted: "bg-emerald-500/15 text-emerald-400 border-emerald-500/25",
    pending: "bg-amber-500/15 text-amber-300 border-amber-500/25",
    skipped: "bg-neutral-500/15 text-neutral-400 border-white/10",
  };
  const label = {
    granted: "Reward granted",
    pending: "Awaiting admin",
    skipped: "Skipped",
  };
  return (
    <span
      className={`text-[10px] px-2 py-0.5 rounded-full uppercase tracking-wider font-semibold border ${styles[status] || styles.pending}`}
    >
      {label[status] || status}
    </span>
  );
}

function fmtRel(iso) {
  if (!iso) return "";
  try {
    const then = new Date(iso).getTime();
    const now = Date.now();
    const diffMin = Math.round((now - then) / 60000);
    if (diffMin < 1) return "just now";
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffHr = Math.round(diffMin / 60);
    if (diffHr < 24) return `${diffHr}h ago`;
    const diffDay = Math.round(diffHr / 24);
    if (diffDay < 30) return `${diffDay}d ago`;
    return new Date(iso).toLocaleDateString();
  } catch { return ""; }
}
